import { useQuery } from "@tanstack/react-query";
import { fetchAttemptedQuizList } from "../services/quizAttemptService";

const attemptColors = ["green", "cyan", "blue", "purple"];

function normalizeAttemptItem(item, index, totalQuestions) {
  const attemptedCount = Number(item?.attempted_count || 0);
  const resolvedQuestionCount = Number(totalQuestions || 0);
  const attemptStatus = String(item?.quiz_action || "Pending").trim();
  const progress =
    resolvedQuestionCount > 0
      ? Math.round((attemptedCount / resolvedQuestionCount) * 100)
      : 0;
  const canReview = attemptStatus.toLowerCase() !== "pending";

  return {
    id: String(index + 1).padStart(2, "0"),
    status: attemptStatus,
    started: item?.attempted_at || "-",
    completed: item?.completed_at || "-",
    rank: item?.rank || "0/0",
    marks: item?.total_marks || "0/0",
    questions: `${attemptedCount}/${resolvedQuestionCount || 0}`,
    progress,
    color: attemptColors[index % attemptColors.length],
    quizId: Number(item?.quiz_id || 0),
    assesmentBatchId: item?.assesment_batch_id || 0,
    questionId: item?.question_id || 0,
    attemptedCount: attemptedCount,
    canReview,
    raw: item,
  };
}

export function useAttemptedQuizList(params = {}, options = {}) {
  return useQuery({
    queryKey: ["attemptedQuizList", params],
    queryFn: async () => {
      const { payload } = await fetchAttemptedQuizList(params);
      const totalQuestions = Number(payload?.total_questions || payload?.question_count || 0);
      const attemptedQuizList = Array.isArray(payload?.attempted_quiz_list)
        ? payload.attempted_quiz_list
        : [];

      return {
        stateName: payload?.state_name || "",
        categoryName: payload?.category_name || "",
        quizId: Number(payload?.quiz_id || params?.quiz_id || 0),
        seriesId: Number(payload?.series_id || 0),
        quizName: payload?.quiz_name || "",
        solvedAt: payload?.solved_at || "",
        quizDescription: payload?.quiz_description || "",
        quizLiveType: payload?.quiz_live_type || "",
        resultDate: payload?.result_date || "",
        totalQuestions,
        startDate: payload?.start_date || "",
        endDate: payload?.end_date || "",
        duration: Number(payload?.duration || 0),
        marksPerQuestion: Number(payload?.marks_per_quest || 0),
        totalMarks: Number(payload?.total_marks || 0),
        totalAttempts: Number(payload?.total_attempts || 0),
        freePaid: payload?.free_paid || "F",
        paymentStatus: Number(payload?.payment_status || 0),
        availableLanguages: payload?.available_languages || "English",
        quizAttemptsLeft: Number(payload?.quiz_attempts_left || 0),
        genericQuizAttemptsLeft: Number(payload?.generic_quiz_attempts_left || 0),
        purchasedQuizAttemptsLeft: Number(payload?.purchased_quiz_attempts_left || 0),
        totalCategoryAttemptsLeft: Number(payload?.total_category_attempts_left || 0),
        dateMessage: payload?.date_message || "",
        questionMessage: payload?.question_message || "",
        quizAttemptMessage: payload?.quiz_attempt_message || "",
        categoryAttemptMessage: payload?.category_attempt_message || "",
        dateStatus: Number(payload?.date_status || 0),
        questionStatus: Number(payload?.question_status || 0),
        questionCount: Number(payload?.question_count || totalQuestions),
        canStart: Number(payload?.can_start || 0) === 1,
        quizStatus: Number(payload?.quiz_status || 0),
        totalAttemptsLeft: Number(payload?.total_attempts_left || 0),
        purchasedReattempts: Number(payload?.purchased_reattempts || 0),
        reattemptMessage: payload?.reattempt_message || "",
        pendingQuizInfo: payload?.pending_quiz_info || null,
        attempts: attemptedQuizList.map((item, index) =>
          normalizeAttemptItem(item, index, totalQuestions)
        ),
        rewardedAdsEnabled: [true, 1, '1'].includes(payload?.rewarded_ads_enabled),
        adCoinBalance: Number(payload?.ad_coin_balance || 0),
        coinUnlockCost: Number(payload?.coin_unlock_cost || 0),
        canUnlockWithAdCoins: [true, 1, '1'].includes(payload?.can_unlock_with_ad_coins),
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    refetchOnReconnect: true,
    placeholderData: (previousData) =>
      previousData ?? {
        stateName: "",
        categoryName: "",
        quizId: 0,
        seriesId: 0,
        quizName: "",
        solvedAt: "",
        quizDescription: "",
        quizLiveType: "",
        resultDate: "",
        totalQuestions: 0,
        startDate: "",
        endDate: "",
        duration: 0,
        marksPerQuestion: 0,
        totalMarks: 0,
        totalAttempts: 0,
        freePaid: "F",
        paymentStatus: 0,
        availableLanguages: "English",
        quizAttemptsLeft: 0,
        genericQuizAttemptsLeft: 0,
        purchasedQuizAttemptsLeft: 0,
        totalCategoryAttemptsLeft: 0,
        dateMessage: "",
        questionMessage: "",
        quizAttemptMessage: "",
        categoryAttemptMessage: "",
        dateStatus: 0,
        questionStatus: 0,
        questionCount: 0,
        canStart: false,
        quizStatus: 0,
        totalAttemptsLeft: 0,
        purchasedReattempts: 0,
        reattemptMessage: "",
        pendingQuizInfo: null,
        attempts: [],
        rewardedAdsEnabled: false,
        adCoinBalance: 0,
        coinUnlockCost: 0,
        canUnlockWithAdCoins: false,
      },
  });
}
